import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createFloorScene } from './floorScene';
import { THEMES } from './themes';

/**
 * Twin3DCanvas — React wrapper for the imperative floorScene engine.
 *
 * Props:
 *   units           scene units (output of unitMapper.mapPayloadToScene().units)
 *   planArea        { w, d } metres
 *   floorPlanUrl    optional faint plan-image underlay
 *   theme           'cyber' | 'teal' | 'ember'
 *   heatmap         'off' | 'footfall' | 'ocr' | 'anomaly'
 *   showGrid        bool
 *   showLabels      bool
 *   glass           0..1 transmission factor
 *   onSelectUnit    (sceneUnit | null) => void
 *   onHoverUnit     (sceneUnit | null) => void
 */
export default function Twin3DCanvas({
    units,
    planArea,
    floorPlanUrl,
    theme = 'cyber',
    heatmap = 'off',
    showGrid = true,
    showLabels = true,
    glass = 0.78,
    onSelectUnit,
    onHoverUnit,
}) {
    const { t } = useTranslation();
    const mountRef = useRef(null);
    const apiRef = useRef(null);
    const themeKeysRef = useRef([]);

    useEffect(() => {
        if (!mountRef.current) return;
        const api = createFloorScene(mountRef.current, {
            theme, units, planArea, floorPlanUrl,
            onSelect: (u) => onSelectUnit && onSelectUnit(u),
            onHover: (u) => onHoverUnit && onHoverUnit(u),
        });
        apiRef.current = api;
        api.setHeatmap(heatmap);
        api.setShowGrid(showGrid);
        api.setShowLabels(showLabels);
        api.setGlass(glass);
        return () => { api.dispose(); apiRef.current = null; };
        // engine handles re-seeding via setUnits/setTheme; only init once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // re-seed units when payload changes
    useEffect(() => {
        if (apiRef.current) apiRef.current.setUnits(units, planArea, floorPlanUrl);
    }, [units, planArea, floorPlanUrl]);

    useEffect(() => { apiRef.current && apiRef.current.setTheme(theme); }, [theme]);
    useEffect(() => { apiRef.current && apiRef.current.setHeatmap(heatmap); }, [heatmap]);
    useEffect(() => { apiRef.current && apiRef.current.setShowGrid(showGrid); }, [showGrid]);
    useEffect(() => { apiRef.current && apiRef.current.setShowLabels(showLabels); }, [showLabels]);
    useEffect(() => { apiRef.current && apiRef.current.setGlass(glass); }, [glass]);

    // apply theme CSS vars to the wrapper (scoped, not :root)
    useEffect(() => {
        const node = mountRef.current?.parentElement;
        if (!node) return;
        themeKeysRef.current.forEach((k) => node.style.removeProperty(k));
        const css = THEMES[theme]?.css || {};
        Object.entries(css).forEach(([k, v]) => node.style.setProperty(k, v));
        themeKeysRef.current = Object.keys(css);
    }, [theme]);

    return (
        <div className="twin3d-stage" aria-label={t('3D floor view')}>
            <div className="twin3d-mount" ref={mountRef} />
        </div>
    );
}
